import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { createDateRangeValidator, validateSum } from '../validators/appointment.validator';
import { BehaviorSubject, catchError, Observable, of, Subject, throwError } from 'rxjs';
import {
  BookingService,
  CustomerSearch,
  CustomerService,
  EmployeeProcedureService,
  EmployeeService,
  getSecondsFromDateTime,
  ServiceService,
  timeStrToSeconds,
  User
} from '@beautyapp/core';
import { filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AddUserComponent, UserVerifyComponent } from '@beautyapp/shared';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { UserViewComponent } from '@beautyapp/users';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { DisableReasonComponent } from '../components/disable-reason/disable-reason.component';
import { deletedServiceNames } from '../visit-modal/deleted-service-error';
import { addSeconds, format } from 'date-fns';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class AppointmentFacade {
  private translateService: TranslateService = inject(TranslateService);
  private customerService: CustomerService = inject(CustomerService);
  private employeeService: EmployeeService = inject(EmployeeService);
  private employeeServiceService: EmployeeProcedureService = inject(EmployeeProcedureService);
  private bookingService: BookingService = inject(BookingService);
  private modalService: NzModalService = inject(NzModalService);
  private drawerService: NzDrawerService = inject(NzDrawerService);
  private serviceService: ServiceService = inject(ServiceService);

  sub$: Subject<any> = new Subject<any>();

  form: FormGroup = new FormGroup(
    {
      id: new FormControl(null),
      cardNo: new FormControl(null),
      employeeId: new FormControl(null),
      onUserRequest: new FormControl(false),
      salaryFixed: new FormControl(null),
      // beaut-424: a share of a price cannot exceed the price. The backend
      // rejects >100 with a 422; without this the operator only discovers that
      // at final save, as a status code instead of a message under the field.
      salaryPercent: new FormControl(null, [
        Validators.min(0),
        Validators.max(100),
      ]),
      userId: new FormControl(null, Validators.required),
      status: new FormControl(1, Validators.required),
      paymentType: new FormControl(null),
      price: new FormControl(0, Validators.required),
      salePrice: new FormControl(null),
      cash: new FormControl(0),
      card: new FormControl(0),
      minutes: new FormControl(0),
      balance: new FormControl(0),
      barter: new FormControl(0),
      consignation: new FormControl(0),
      bankTransfer: new FormControl(0),
      fixedPrice: new FormControl(null),
      bookingDate: new FormControl(null, Validators.required),
      startTime: new FormControl(null, Validators.required),
      endTime: new FormControl(null, Validators.required),
      isGift: new FormControl(false),
      sendNotification: new FormControl(true),
      isConsignation: new FormControl(false),
      description: new FormControl(null),
      insuranceId: new FormControl(null),
      insurancePercent: new FormControl(null),
      insurancePolicy: new FormControl(null)
    },
    {
      validators: [createDateRangeValidator(), validateSum()]
    }
  );

  // Rebuilt reactively on onLangChange / onTranslationChange so the dropdown
  // never shows raw keys (PAYMENTTYPE.1) when the translation bundle hasn't
  // loaded yet at facade-construction time after a hard reload.
  paymentTypes: { id: number; name: string }[] = this.buildPaymentTypes();

  private readonly _paymentTypesSync = this.translateService.onLangChange
    .pipe(untilDestroyed(this))
    .subscribe(() => (this.paymentTypes = this.buildPaymentTypes()));
  private readonly _paymentTypesTranslationSync =
    this.translateService.onTranslationChange
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.paymentTypes = this.buildPaymentTypes()));
  private readonly _paymentTypesDefaultLangSync =
    this.translateService.onDefaultLangChange
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.paymentTypes = this.buildPaymentTypes()));

  private buildPaymentTypes(): { id: number; name: string }[] {
    const t = (key: string) => this.translateService.instant(key);
    return [
      { id: 1, name: t('PAYMENTTYPE.1') },
      { id: 2, name: t('PAYMENTTYPE.2') },
      { id: 3, name: t('PAYMENTTYPE.3') },
      { id: 4, name: t('PAYMENTTYPE.4') },
      { id: 5, name: t('PAYMENTTYPE.5') },
      { id: 6, name: t('PAYMENTTYPE.6') },
      { id: 7, name: t('PAYMENTTYPE.7') },
      { id: 8, name: t('PAYMENTTYPE.8') },
    ];
  }

  employee$: Observable<any> = this.employeeService.getDropdown().pipe(
    switchMap((res) => {
      return of(
        res.map((m: any) => ({
          ...m,
          fullName: m.user?.fullName || ''
        }))
      );
    })
  );

  currentUser = new BehaviorSubject<User>({} as User);
  selectedCustomer$: Observable<User> = this.currentUser.asObservable();

  currentEmployee = new BehaviorSubject<any>({} as any);
  selectedEmployee$: Observable<any> = this.currentEmployee.asObservable();
  prevEmployee: any;
  onlineBookingId?: string;

  get getCurrentEmployee() {
    return this.currentEmployee.getValue();
  }

  employeeServices = new BehaviorSubject<any[]>([]);
  employeeServices$: Observable<any[]> = this.employeeServices.asObservable();

  services = new BehaviorSubject<any[]>([]);
  services$: Observable<any[]> = this.services.asObservable();

  get getServices() {
    return this.services.getValue();
  }

  specServices = new BehaviorSubject<any[]>([]);
  specServices$: Observable<any[]> = this.specServices.asObservable();

  get getSpecServices() {
    return this.specServices.getValue();
  }

  isLoading = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this.isLoading.asObservable();

  // Shared customer-search state (search + stable customers$ + reset). Own
  // instance per facade — see CustomerSearch (a shared root singleton would bleed
  // the visit flow's list into the booking flow).
  private readonly customerSearch = new CustomerSearch(this.customerService);
  customers$: Observable<User[]> = this.customerSearch.customers$;

  _modalRef!: NzModalRef;

  get modalRef() {
    return this._modalRef;
  }

  set modalRef(modalRef: NzModalRef) {
    this._modalRef = modalRef;
  }

  get status() {
    return this.form.get('status')?.value;
  }

  get paymentType() {
    return this.form.get('paymentType')?.value;
  }

  get startTime() {
    const startTime = this.form.get('startTime')?.value;
    return format(new Date(startTime), 'yyyy-MM-dd HH:mm');
  }

  get endTime() {
    const endTime = this.form.get('endTime')?.value;
    return format(new Date(endTime), 'yyyy-MM-dd HH:mm');
  }

  get bookingId() {
    return this.form.get('id')?.value;
  }

  set bookingId(id: string) {
    this.form.get('id')?.setValue(id);
  }

  _booking: any;
  get booking(): any {
    return this._booking;
  }

  set booking(booking: any) {
    this._booking = booking;
  }

  get userId() {
    return this.form.get('userId')?.value;
  }

  set userId(userId: string) {
    this.form.get('userId')?.setValue(userId);
  }

  get employeeId() {
    return this.form.get('employeeId')?.value;
  }

  set employeeId(employeeId: string) {
    this.form.get('employeeId')?.setValue(employeeId);
  }

  set user(user: User) {
    this.userId = user.id;
    this.currentUser.next(user);
  }

  get user(): User {
    return this.currentUser.getValue();
  }

  get invalid() {
    return this.form.invalid;
  }

  public get checkBookingInFuture() {
    return (
      new Date(format(new Date(), 'yyyy-MM-dd 23:59:59')).getTime() <
      new Date(this.form.value.bookingDate).getTime()
    );
  }

  set dates(date: string) {
    this.form
      .get('bookingDate')
      ?.setValue(format(new Date(date), 'yyyy-MM-dd'));
    this.form
      .get('startTime')
      ?.setValue(format(new Date(date), 'yyyy-MM-dd HH:mm'));
    // const endTime = this.form
    //   .get('endTime')?.value
    // !endTime && this.form
    //   .get('endTime')
    //   ?.setValue(format(new Date(addMinutes(new Date(date), 15)), 'yyyy-MM-dd HH:mm'));
  }

  get checkComplete() {
    const paymentType = this.form.value.paymentType;
    const cash = this.form.value.cash;
    const card = this.form.value.card;
    const minutes = this.form.value.minutes;
    const balance = this.form.value.balance;
    const barter = this.form.value.barter;
    const consignation = this.form.value.consignation;
    const status = this.form.value.status;
    return (
      this.form.valid &&
      status === 1 &&
      paymentType &&
      (cash >= 0 ||
        card >= 0 ||
        minutes >= 0 ||
        balance >= 0 ||
        barter >= 0 ||
        consignation >= 0)
    );
  }

  getServiceSum$ = (): Observable<number> => {
    return this.services$.pipe(
      map((res) => {
        return res.reduce((sum, serv: any) => {
          const { salePrice, price } = serv;
          const priceTemp = +salePrice > 0 ? +salePrice : +price;
          return priceTemp + sum;
        }, 0);
      })
    );
  };

  init() {
    // this.form.patchValue({
    //   status: 1,
    //   sendNotification: true,
    //   onUserRequest: false,
    //   isGift: false,
    //   isConsignation: false,
    // });
    // this.form
    //   .get('userId')
    //   ?.valueChanges.pipe(
    //     untilDestroyed(this),
    //     filter((res) => !!res),
    //     mergeMap((res) => this.getCustomerById()),
    //   )
    //   .subscribe();

    this.form
      .get('employeeId')
      ?.valueChanges.pipe(
      untilDestroyed(this),
      filter((res) => !!res),
      switchMap((res: any) => {
        if (res) {
          this.prevEmployee = this.getCurrentEmployee;
          return this.getSelectedEmployee();
        }
        return of(null);
      })
    )
      .subscribe();

    this.form
      .get('paymentType')
      ?.valueChanges.pipe(
      untilDestroyed(this),
      switchMap((res) => this.getServiceSum$()),
      tap((res) => {
        this.paymentUpdate(res);
      })
    )
      .subscribe();
  }

  getById(): Observable<any> {
    this.isLoading.next(true);
    return this.bookingService.getById(this.bookingId).pipe(
      map((res) => {
        this.booking = res;
        this.userId = res.userId;
        this.services.next(res.services);
        this.specServices.next(res.specServices);
        this.currentUser.next(res.user);
        this.currentEmployee.next(res.employee);
        this.form.patchValue({
          ...res,
          startTime: format(
            new Date(`${res.bookingDate} ${res.startTime}`),
            'yyyy-MM-dd HH:mm'
          ),
          endTime: format(
            new Date(`${res.bookingDate} ${res.endTime}`),
            'yyyy-MM-dd HH:mm'
          )
        });
        this.form.updateValueAndValidity();
        return res;
      }),
      tap(() => {
        this.isLoading.next(false);
      }),
      catchError((err) => {
        this.isLoading.next(false);
        return throwError(() => err);
      })
    );
  }

  getCustomerById() {
    return this.customerService.getById(this.form.get('userId')?.value).pipe(
      tap((res) => {
        if (res) {
          this.currentUser.next(res);
        }
      })
    );
  }

  getServiceByIds(ids: string[]) {
    return this.serviceService.getByIds(ids);
  }

  searchCustomer(search: any) {
    // beaut-366 fix now lives in the shared CustomerSearch helper (empty term
    // loads the first page; results push into a stable customers$; reset() clears
    // it between bookings). See CustomerSearch.
    this.customerSearch.search(search);
  }

  getSelectedEmployee(): Observable<any> {
    console.log(this.employeeId);
    if (!this.employeeId) return of(null);
    return this.employeeService.getById(this.employeeId).pipe(
      untilDestroyed(this),
      filter((res) => !!res),
      tap((res) => {
        console.log(res);
        this.currentEmployee.next(res);
        this.form.get('salaryPercent')?.setValue(res.outputPercent);
      }),
      switchMap((res) => this.getEmployeeServices()),
      tap((employeeServices) => {
        console.log(employeeServices);
        if (
          this.prevEmployee &&
          this.prevEmployee.id !== this.getCurrentEmployee.id
        ) {
          const services = this.getServices.map((service) => {
            if (service?.employeeId === this.prevEmployee?.id) {
              let outputPercent = this.getCurrentEmployee?.outputPercent;
              let salaryFixed = 0;
              if (employeeServices && employeeServices.length) {
                const find = employeeServices.find(
                  (s: any) => s.serviceId === service.serviceId
                );
                if (find) {
                  outputPercent = find.outputPercent;
                  salaryFixed = find.fixedSalary;
                }
              }
              return {
                ...service,
                employeeId: this.getCurrentEmployee?.id,
                employeeName: this.getCurrentEmployee?.user?.fullName,
                salaryPercent: outputPercent,
                salaryFixed: salaryFixed
              };
            }
            return service;
          });
          this.services.next(services);
        }
      })
    );
  }

  private getEmployeeServices(): Observable<any> {
    if (!this.employeeId) return of(null);
    return this.employeeServiceService.getByEmployee(this.employeeId).pipe(
      tap((res) => {
        this.employeeServices.next(res);
      })
    );
  }

  paymentUpdate(price: number) {
    switch (this.paymentType) {
      case 1:
      case '1':
        this.form
          .get('cash')
          ?.setValidators([
            Validators.required,
            Validators.min(1),
            Validators.max(price)
          ]);
        this.form.patchValue({
          minutes: 0,
          balance: 0,
          cash: this.booking && this.booking.cash ? this.booking.cash : price,
          card: 0,
          barter: 0,
          consignation: 0,
          bankTransfer: 0
        });
        this.form.get('minutes')?.clearValidators();
        this.form.get('balance')?.clearValidators();
        this.form.get('card')?.clearValidators();
        this.form.get('barter')?.clearValidators();
        this.form.get('consignation')?.clearValidators();
        this.form.get('bankTransfer')?.clearValidators();
        if (!this.booking) {
          this.form.get('cash')?.setValue(price);
        } else if (this.booking?.cash === 0) {
          this.form.get('cash')?.setValue(price);
        }
        break;
      case 2:
      case '2':
        this.form
          .get('card')
          ?.setValidators([
            Validators.required,
            Validators.min(1),
            Validators.max(price)
          ]);
        this.form.patchValue({
          minutes: 0,
          balance: 0,
          cash: 0,
          card: this.booking && this.booking.card ? this.booking.card : price,
          barter: 0,
          consignation: 0,
          bankTransfer: 0
        });
        this.form.get('minutes')?.clearValidators();
        this.form.get('balance')?.clearValidators();
        this.form.get('cash')?.clearValidators();
        this.form.get('barter')?.clearValidators();
        this.form.get('consignation')?.clearValidators();
        this.form.get('bankTransfer')?.clearValidators();
        if (!this.booking) {
          this.form.get('card')?.setValue(price);
        } else if (this.booking?.cash === 0) {
          this.form.get('card')?.setValue(price);
        }
        break;
      case 3:
      case '3':
        this.form
          .get('cash')
          ?.setValidators([
            Validators.required,
            Validators.min(1),
            Validators.max(price)
          ]);
        this.form
          .get('card')
          ?.setValidators([
            Validators.required,
            Validators.min(1),
            Validators.max(price)
          ]);
        this.form.patchValue({
          minutes: 0,
          balance: 0,
          barter: 0,
          cash: this.booking && this.booking.cash ? this.booking.cash : 0,
          card: this.booking && this.booking.card ? this.booking.card : 0,
          consignation: 0,
          bankTransfer: 0
        });
        this.form.get('minutes')?.clearValidators();
        this.form.get('balance')?.clearValidators();
        this.form.get('barter')?.clearValidators();
        this.form.get('consignation')?.clearValidators();
        this.form.get('bankTransfer')?.clearValidators();
        break;
      case 4:
      case '4':
        this.form
          .get('minutes')
          ?.setValidators([
            Validators.required,
            Validators.min(0),
            Validators.max(this.user.minutes || 0)
          ]);
        this.form.patchValue({
          minutes:
            this.booking && this.booking.minutes ? this.booking.minutes : 0,
          balance: 0,
          cash: 0,
          card: 0,
          barter: 0,
          consignation: 0,
          bankTransfer: 0
        });
        this.form.get('balance')?.clearValidators();
        this.form.get('barter')?.clearValidators();
        this.form.get('consignation')?.clearValidators();
        this.form.get('bankTransfer')?.clearValidators();
        this.form.get('cash')?.clearValidators();
        this.form.get('card')?.clearValidators();
        break;
      case 5:
      case '5':
        this.form
          .get('balance')
          ?.setValidators([
            Validators.required,
            Validators.min(0),
            Validators.max(this.user.balance || 0)
          ]);
        this.form.patchValue({
          balance:
            this.booking && this.booking.balance ? this.booking.balance : 0,
          minutes: 0,
          cash: this.booking && this.booking.cash ? this.booking.cash : 0,
          card: this.booking && this.booking.card ? this.booking.card : 0,
          barter: 0,
          consignation: 0,
          bankTransfer: 0
        });
        this.form.get('minutes')?.clearValidators();
        this.form.get('cash')?.clearValidators();
        this.form.get('card')?.clearValidators();
        this.form.get('barter')?.clearValidators();
        this.form.get('consignation')?.clearValidators();
        this.form.get('bankTransfer')?.clearValidators();
        break;
      case 6:
      case '6':
        this.form
          .get('barter')
          ?.setValidators([
            Validators.required,
            Validators.min(0),
            Validators.max(price)
          ]);
        this.form.patchValue({
          barter: this.booking && this.booking.barter ? this.booking.barter : 0,
          minutes: 0,
          balance: 0,
          cash: 0,
          card: 0,
          consignation: 0,
          bankTransfer: 0
        });
        this.form.get('minutes')?.clearValidators();
        this.form.get('balance')?.clearValidators();
        this.form.get('cash')?.clearValidators();
        this.form.get('card')?.clearValidators();
        this.form.get('consignation')?.clearValidators();
        this.form.get('bankTransfer')?.clearValidators();
        break;
      case 7:
      case '7':
        this.form.patchValue({ cash: null, card: null, consignation: price });
        this.form
          .get('consignation')
          ?.setValidators([Validators.required, Validators.min(0)]);
        this.form.patchValue({
          consignation:
            this.booking && this.booking.consignation
              ? this.booking.consignation
              : 0,
          cash: this.booking && this.booking.cash ? this.booking.cash : 0,
          card: this.booking && this.booking.card ? this.booking.card : 0,
          minutes: 0,
          balance: 0,
          barter: 0,
          bankTransfer: 0
        });
        this.form.get('cash')?.clearValidators();
        this.form.get('card')?.clearValidators();
        this.form.get('minutes')?.clearValidators();
        this.form.get('balance')?.clearValidators();
        this.form.get('barter')?.clearValidators();
        this.form.get('bankTransfer')?.clearValidators();
        break;
      case 8:
      case '8':
        this.form
          .get('bankTransfer')
          ?.setValidators([Validators.required, Validators.min(0)]);
        this.form.patchValue({
          bankTransfer:
            this.booking && this.booking.bankTransfer
              ? this.booking.bankTransfer
              : price,
          minutes: 0,
          balance: 0,
          cash: 0,
          card: 0,
          barter: 0,
          consignation: 0
        });
        this.form.get('minutes')?.clearValidators();
        this.form.get('balance')?.clearValidators();
        this.form.get('cash')?.clearValidators();
        this.form.get('card')?.clearValidators();
        this.form.get('barter')?.clearValidators();
        this.form.get('consignation')?.clearValidators();
        if (!this.booking) {
          this.form.get('bankTransfer')?.setValue(price);
        } else if (this.booking?.bankTransfer === 0) {
          this.form.get('bankTransfer')?.setValue(price);
        }
        break;
      default:
        this.form.patchValue({
          cash: 0,
          card: 0,
          minutes: 0,
          balance: 0,
          barter: 0,
          consignation: 0,
          bankTransfer: 0
        });
        this.form.get('cash')?.clearValidators();
        this.form.get('card')?.clearValidators();
        this.form.get('minutes')?.clearValidators();
        this.form.get('balance')?.clearValidators();
        this.form.get('barter')?.clearValidators();
        this.form.get('consignation')?.clearValidators();
        this.form.get('bankTransfer')?.clearValidators();
        break;
    }
    this.form.get('cash')?.updateValueAndValidity();
    this.form.get('card')?.updateValueAndValidity();
    this.form.get('minutes')?.updateValueAndValidity();
    this.form.get('balance')?.updateValueAndValidity();
    this.form.get('barter')?.updateValueAndValidity();
    this.form.get('consignation')?.updateValueAndValidity();
    this.form.get('bankTransfer')?.updateValueAndValidity();
  }

  addNewUser() {
    const modal = this.modalService.create({
      nzTitle: this.translateService.instant('calendar.createUser'),
      nzContent: AddUserComponent,
      nzWidth: '900px',
      nzStyle: { top: '0px' },
      nzData: {
        userId: this.userId
      }
    });

    modal.afterClose
      .pipe(
        takeUntil(this.sub$)
      )
      .subscribe((res) => {
        if (res) {
          this.user = res;
        }
      });
  }

  viewUser() {
    let title = `${this.user.firstname} ${this.user.lastname || ''}`;
    if (this.user.cardNo) {
      title += ` - # ${this.user.cardNo}`;
    }
    this.drawerService.create<UserViewComponent, { data: string }, string>({
      nzTitle: title,
      nzContent: UserViewComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: {
        padding: '0 15px 15px 15px'
      },
      nzData: {
        data: this.user.id
      }
    });
  }

  addNewService(service: any): Observable<any> {
    const services = this.services.getValue();
    services.push(service);
    this.services.next(services);
    const startSeconds = getSecondsFromDateTime(this.startTime);
    const totalSeconds = services.reduce((seconds, serv) => {
      return timeStrToSeconds(serv.time) + seconds;
    }, 0);
    const totalPrice = services.reduce((seconds, serv) => {
      return +serv.price + seconds;
    }, 0);

    const totalSalePrice = services.reduce((a: any, b: any) => {
      if (b.salePrice === null || b.salePrice === undefined || b.salePrice === '' || b.salePrice === 0) {
        return a + +b.price;
      }
      return a + +b.salePrice;
    }, 0);

    const totalExpence = services.reduce((seconds, serv) => {
      return +serv.expence + seconds;
    }, 0);
    this.form.patchValue({
      fixedPrice: totalExpence,
      price: totalPrice,
      salePrice: totalSalePrice,
      endTime: format(
        addSeconds(new Date(this.startTime), totalSeconds * 60),
        'yyyy-MM-dd HH:mm'
      )
    });
    return of(service);
  }

  editService(service: any): Observable<any> {
    const services = this.services.getValue();
    const index = service.serviceIndex;
    services[index] = service;
    this.services.next(services);
    const startSeconds = getSecondsFromDateTime(this.startTime);
    const totalSeconds = services.reduce((seconds, serv) => {
      return timeStrToSeconds(serv.time) + seconds;
    }, 0);
    const totalPrice = services.reduce((seconds, serv) => {
      return +serv.price + seconds;
    }, 0);
    const totalSalePrice = services.reduce((a: any, b: any) => {
      if (b.salePrice === null || b.salePrice === undefined || b.salePrice === '' || b.salePrice === 0) {
        return a + +b.price;
      }
      return a + +b.salePrice;
    }, 0);
    const totalExpence = services.reduce((seconds, serv) => {
      return +serv.expence + seconds;
    }, 0);
    this.form.patchValue({
      fixedPrice: totalExpence,
      price: totalPrice,
      salePrice: totalSalePrice,
      endTime: format(
        addSeconds(new Date(this.startTime), totalSeconds * 60),
        'yyyy-MM-dd HH:mm'
      )
    });
    return of(service);
  }

  deleteService(service: any, serviceIndex: number): Observable<any> {
    const services = this.services.getValue();
    services.splice(serviceIndex, 1);
    this.services.next(services);
    const startSeconds = getSecondsFromDateTime(this.startTime);
    const totalSeconds = services.reduce((seconds, serv) => {
      return timeStrToSeconds(serv.time) + seconds;
    }, 0);
    const totalPrice = services.reduce((seconds, serv) => {
      return +serv.price + seconds;
    }, 0);
    const totalSalePrice = services.reduce((a: any, b: any) => {
      if (b.salePrice === null || b.salePrice === undefined || b.salePrice === '' || b.salePrice === 0) {
        return a + +b.price;
      }
      return a + +b.salePrice;
    }, 0);
    const totalExpence = services.reduce((seconds, serv) => {
      return +serv.expence + seconds;
    }, 0);
    this.form.patchValue({
      fixedPrice: totalExpence,
      price: totalPrice,
      salePrice: totalSalePrice,
      endTime: format(
        addSeconds(new Date(this.startTime), totalSeconds * 60),
        'yyyy-MM-dd HH:mm'
      )
    });
    if (!services.length) {
      this.form.patchValue({
        endTime: null
      });
    }
    return of(service);
  }

  submit(): Observable<any> {
    this.form.markAllAsTouched();
    if (this.form.invalid) return of(null);
    this.isLoading.next(true);
    if (this.bookingId) {
      const params = {
        booking: {
          ...this.form.value,
          startTime: this.startTime,
          endTime: this.endTime
        },
        services: this.getServices,
        specServices: this.getSpecServices
      };
      return this.bookingService.updateNew(this.bookingId, params).pipe(
        finalize(() => {
          this.modalRef.close(true);
          this.isLoading.next(false);
        })
      );
    } else {
      const params = {
        booking: {
          ...this.form.value,
          startTime: this.startTime,
          endTime: this.endTime
        },
        services: this.getServices,
        specServices: this.getSpecServices,
        onlineBookingId: this.onlineBookingId
      };
      return this.bookingService.createNew(params).pipe(
        tap((res) => {
          this.modalRef.close(true);
          this.isLoading.next(false);
        })
      );
    }
  }

  complete(): Observable<any> {
    this.form.markAllAsTouched();
    if (this.form.invalid) return of(null);

    if ((this.paymentType !== 4 && this.paymentType !== '4') && (this.paymentType !== 6 && this.paymentType !== '6')) {

      const servicePrices = this.getServices.reduce((sum, serv) => {
        if (serv.salePrice > 0) {
          return +serv.salePrice + sum;
        }
        return +serv.price + sum;
      }, 0);

      const payed = +this.form.value.cash + +this.form.value.card + +this.form.value.balance + +this.form.value.consignation + +this.form.value.bankTransfer;

      if (servicePrices !== payed) {
        this.modalService.error({
          nzTitle: 'პროცედურების ფასი და გადახდის ჯამი არ ემთხვევა',
          nzContent: `პროცედურების ფასი: ${servicePrices} გადახდის ჯამი: ${payed}`
        });
        return of(null);
      }
    }

    this.isLoading.next(true);
    if (this.bookingId) {
      const params = {
        booking: {
          ...this.form.value,
          startTime: this.startTime,
          endTime: this.endTime,
          status: 2
        },
        services: this.getServices,
        specServices: this.getSpecServices
      };
      return this.bookingService.updateNew(this.bookingId, params).pipe(
        // beaut-635: the backend refuses to complete while a line of the visit
        // is on a deleted service. Explain why, like the visit modal does.
        catchError((err) => {
          const names = deletedServiceNames(err);
          if (names !== null) {
            const title = this.translateService.instant(
              'addVisit.errors.deletedServiceTitle'
            );
            // No names in the refusal: show the server's text, not an empty list.
            const content = names.length
              ? this.translateService.instant(
                  'addVisit.errors.deletedServiceContent',
                  { names: names.join(', ') }
                )
              : err?.error?.message || title;
            this.modalService.error({ nzTitle: title, nzContent: content });
          }
          return throwError(() => err);
        }),
        finalize(() => {
          this.modalRef.close(true);
          this.isLoading.next(false);
        })
      );
    } else {
      const params = {
        booking: {
          ...this.form.value,
          startTime: this.startTime,
          endTime: this.endTime,
          status: 2
        },
        services: this.getServices,
        specServices: this.getSpecServices
      };
      return this.bookingService.createNew(params).pipe(
        finalize(() => {
          this.modalRef.close(true);
          this.isLoading.next(false);
        })
      );
    }
  }

  deleteBooking() {
    this.modalService.create({
      nzTitle: 'ვიზიტის გაუქმება',
      nzContent: DisableReasonComponent,
      nzWidth: '600px',
      nzStyle: { top: '0px' },
      nzData: {
        bookingId: this.bookingId
      },
      nzOnOk: () => {
        this.modalRef.triggerOk().then();
      }
    });
  }

  modalClose() {
    this.modalRef.close();
  }

  destroy() {
    this.sub$.next(null);
    this.sub$.complete();
    this.services.next([]);
    this.specServices.next([]);
    this.isLoading.next(false);
    this.currentUser.next({} as User);
    this.currentEmployee.next({} as any);
    this.employeeServices.next([]);
    this.customerSearch.reset();
    this.booking = null;
    this.form.patchValue({
      id: null,
      cardNo: null,
      employeeId: null,
      onUserRequest: false,
      salaryFixed: null,
      salaryPercent: null,
      userId: null,
      status: 1,
      paymentType: null,
      price: 0,
      salePrice: null,
      cash: 0,
      card: 0,
      minutes: 0,
      balance: 0,
      barter: 0,
      consignation: 0,
      bankTransfer: 0,
      fixedPrice: null,
      bookingDate: null,
      startTime: null,
      endTime: null,
      isGift: false,
      sendNotification: true,
      isConsignation: false,
      description: null,
      insuranceId: null,
      insurancePercent: null,
      insurancePolicy: null
    });
    this.modalRef.destroy();
  }

  updateFixedPrice(product: any) {
    const fixedPrice = this.form.get('fixedPrice')?.value;
    this.form.patchValue({
      fixedPrice: fixedPrice + (product.price || 0)
    });
  }

  selectCustomer($event: any) {
    console.log($event);
    this.userId = $event.id;
    this.currentUser.next($event);
  }

  verify(user: User) {
    console.log(user);

    if (user.isVerified) {
      return;
    }

    this.modalService.create({
      nzTitle: this.translateService.instant('users.verifyUser'),
      nzContent: UserVerifyComponent,
      nzWidth: '400px',
      nzData: {
        userId: user.id
      },
      nzOnOk: () => {
        this.getCustomerById()
          .pipe(
            untilDestroyed(this)
          )
          .subscribe((res) => {
            console.log(res);
            this.user = res;
          });
      }
    });
  }
}
